import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  normalizeRosterPlayer,
  sortRosterPlayers,
  staticRosterAsDocs,
  listAllRosterPlayers,
  getPlayersForTeam,
  findRosterPlayer,
  findRosterPlayerById,
} from '../utils/rosterCatalog';

/**
 * Live roster = Firestore rosterPlayers overlaid on the built-in catalog.
 * - Firestore docs win for matching ids
 * - Built-in players stay visible until they exist in Firestore (so adding one
 *   player never wipes the rest of the roster)
 * - Soft-deleted docs (active === false) stay hidden and block static resurrection
 */
export default function useRosterCatalog() {
  const [players, setPlayers] = useState(() => staticRosterAsDocs());
  const [source, setSource] = useState('static');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'rosterPlayers'),
      (snap) => {
        const staticDocs = staticRosterAsDocs();
        if (snap.empty) {
          setPlayers(staticDocs);
          setSource('static');
          setLoading(false);
          return;
        }

        const fromFs = snap.docs.map((d) => normalizeRosterPlayer({ id: d.id, ...d.data() }));
        // Any Firestore id (active or not) blocks the built-in copy of that player
        const presentIds = new Set(fromFs.map((p) => p.id));
        const activeFs = fromFs.filter((p) => p.active !== false);
        const fromStatic = staticDocs.filter((p) => !presentIds.has(p.id));
        const merged = sortRosterPlayers([...activeFs, ...fromStatic]);

        setPlayers(merged);
        if (fromStatic.length === 0) setSource('firestore');
        else if (activeFs.length === 0) setSource('static');
        else setSource('hybrid');
        setLoading(false);
      },
      (err) => {
        console.error('rosterPlayers listen:', err);
        setPlayers(staticRosterAsDocs());
        setSource('static');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const players2013 = useMemo(() => getPlayersForTeam('2013', players), [players]);
  const players2014 = useMemo(() => getPlayersForTeam('2014', players), [players]);
  const allOptions = useMemo(() => listAllRosterPlayers(players), [players]);

  return {
    players,
    players2013,
    players2014,
    allOptions,
    source,
    loading,
    findPlayer: (teamId, name) => findRosterPlayer(teamId, name, players),
    getById: (id) => findRosterPlayerById(id, players),
  };
}
